import {BrowserModule} from '@angular/platform-browser';
import {NgModule, ErrorHandler} from '@angular/core';

import {AppComponent} from './app.component';
import {CommonModule, LocationStrategy, PathLocationStrategy} from '@angular/common';
import {AppRoutingModule} from './app-routing.module';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {BrowserAnimationsModule, NoopAnimationsModule} from '@angular/platform-browser/animations';
import {environment} from '../environments/environment';
import {Router, RouteReuseStrategy} from '@angular/router';
import {ToastModule} from 'primeng/toast';
import {ConfirmationService, MessageService} from 'primeng/api';
import {DragulaModule} from 'ng2-dragula';
import {WebmateOptions} from '@webmate-web-core/webmate-options';
import {ResourceFactory} from '@webmate-web-core/shared/components/resources/resource-factory.service';
import {AuthenticationModule} from '@webmate-web-core/authentication/authentication.module';
import {NotificationModule} from '@webmate-web-core/shared/components/notification/notification.module';
import {DialogModule} from '@webmate-web-core/shared/components/dialog/dialog.module';
import { fas } from '@fortawesome/free-solid-svg-icons';
import {NavigationModule} from '@webmate-web-core/navigation/navigation.module';
import {WebmateErrorHandler} from '@webmate-web-core/errorhandling/webmate.errorhandler';
import {ApiAuthInterceptor} from '@webmate-web-core/shared/services/interceptors/api-auth.interceptor';
import {AuthService, AuthStorageStrategy, LocalStorageAuthStorageStrategy} from '@webmate-web-core/shared/services/auth.service';
import {SearchApi} from '@webmate-web-core/shared/api/rest/search.restapi';
import {TranslateLoader, TranslateModule} from "@ngx-translate/core";
import {TranslateHttpLoader} from "@ngx-translate/http-loader";
import {TestRouteReuseStrategy} from '@webmate-web-core/shared/routing/test-route-reuse.strategy';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {ValueShareService} from "@webmate-web-core/shared/services/valueshare.service";
import {DialogService} from 'primeng/dynamicdialog';
import {BlobErrorHttpInterceptor} from "@webmate-web-core/shared/error-handling/api-blob-error.interceptor";
import {MobileAccessActivationComponent} from '@webmate-web-core/misc/mobileaccess-activation.component';
import {BrowserPersistenceService, PersistenceService} from '@webmate-web-core/shared/services/persistence.service';
import {providePrimeNG} from 'primeng/config';
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async';
import {WMPreset} from './themePresets';
import {ApiTrackingInterceptor} from '@webmate-web-core/shared/error-handling/api-tracking.interceptor';
import {TrackClicksDirective} from './track-clicks.directive';
import {INTERACTION_PATH_OPENER} from '@webmate-web-core/shared/services/interactionpath-opener.service';
import {WebmateInteractionPathOpenerService} from '@webmate-web-core/shared/services/webmate-interactionpath-opener.service';
import {TrackingEventService} from '@webmate-web-core/shared/services/tracking-event.service';
import {
    TESTOBJECT_EXPEDITION_OPENER,
    WebmateTestObjectExpeditionOpenerService
} from '@webmate-web-core/shared/services/testobject-expedition-opener.service';

export function webmateOptionsFactory() {
    return new WebmateOptions(environment);
}

export function resourceFactoryFactory() {
    return new ResourceFactory();
}

export function createTranslateLoader(http: HttpClient) {
    return new TranslateHttpLoader(http, '/assets/i18n/', '.json');
}

@NgModule({ declarations: [
        AppComponent,
        TrackClicksDirective
    ],
    bootstrap: [AppComponent],
    imports: [
        CommonModule,
        BrowserModule,
        ToastModule,
        BrowserAnimationsModule,
        DragulaModule.forRoot(),
        AuthenticationModule,
        NotificationModule,
        DialogModule,
        NavigationModule,
        TranslateModule.forRoot({
            defaultLanguage: 'en',
            loader: {
                provide: TranslateLoader,
                useFactory: (createTranslateLoader),
                deps: [HttpClient]
            }
        }),
        FontAwesomeModule,
        AppRoutingModule,
        // Standalone Components
        MobileAccessActivationComponent
    ],
    providers: [
        { provide: AuthStorageStrategy, useClass: LocalStorageAuthStorageStrategy },
        { provide: PersistenceService, useClass: BrowserPersistenceService },
        { provide: ResourceFactory, useFactory: resourceFactoryFactory, deps: [] },
        { provide: WebmateOptions, useFactory: webmateOptionsFactory, deps: [] },
        { provide: ErrorHandler, useClass: WebmateErrorHandler },
        { provide: ConfirmationService },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ApiAuthInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: BlobErrorHttpInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ApiTrackingInterceptor,
            multi: true
        },
        {
            provide: INTERACTION_PATH_OPENER,
            useClass: WebmateInteractionPathOpenerService,
        },
        {
            provide: TESTOBJECT_EXPEDITION_OPENER,
            useClass: WebmateTestObjectExpeditionOpenerService,
        },
        {
            provide: RouteReuseStrategy,
            useClass: TestRouteReuseStrategy
        },
        TrackingEventService,
        MessageService,
        WebmateErrorHandler,
        SearchApi,
        BrowserPersistenceService,
        // HttpService,
        // XHRBackend,
        ValueShareService,
        { provide: LocationStrategy, useClass: PathLocationStrategy },
        DialogService,
        provideHttpClient(withInterceptorsFromDi()),
        provideAnimationsAsync(),
        providePrimeNG({
            theme: {
                preset: WMPreset,
                options: {
                    darkModeSelector: '.wm-w-theme-dark'
                },
            },
        }),
    ] })
export class AppModule {
    constructor(library: FaIconLibrary) {
        library.addIconPacks(fas)
    }
}
